/* fmt.ts — the formatters and the vocabulary, carried across from the SQLite app verbatim.
 *
 * These are ported rather than rewritten because several of them ARE decisions, not conveniences:
 * `distChip` states that a zero is not a measurement, `PLACED_NOTE` discloses which lens a count is
 * over, and `SQ`/`MONEY` choose the same abbreviation points the old screens used. A "tidier"
 * version of any of them changes what a number on screen claims.
 */

export const N = (v: unknown): string =>
  (v == null || v === '' ? '' : Number(v).toLocaleString('en-US'));

export const N1 = (v: unknown): string =>
  (v == null ? '' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 }));

/* Abbreviated sq ft / counts. SQ(0) is an em-dash on purpose: a slider at "no minimum" showing "0"
   reads as a broken value rather than as "any". */
export const SQ = (v: unknown): string => {
  const n = Number(v);
  if (!n) return '—';
  return n >= 1e9 ? (n / 1e9).toFixed(2) + 'B'
    : n >= 1e6 ? (n / 1e6).toFixed(n >= 1e7 ? 0 : 1) + 'M'
      : n >= 1e3 ? Math.round(n / 1e3) + 'k' : String(n);
};

export const MONEY = (v: unknown): string => {
  const n = Number(v);
  if (!n) return '—';
  return n >= 1e9 ? '$' + (n / 1e9).toFixed(n >= 1e11 ? 0 : 1) + 'B'
    : n >= 1e6 ? '$' + (n / 1e6).toFixed(n >= 1e8 ? 0 : 1) + 'M'
      : n >= 1e3 ? '$' + Math.round(n / 1e3) + 'k' : '$' + N(n);
};

export const pct = (a: number, b: number): string =>
  (!b ? '0%' : (100 * a / b).toFixed(a / b < 0.1 ? 1 : 0) + '%');

/* https-only. A vendor URL going straight into an href is a scheme risk — `javascript:` in a
   linkedin_url column is possible, and the column is vendor-supplied. */
export const safeUrl = (u: unknown): string =>
  (/^https:\/\//i.test(String(u || '').trim()) ? String(u).trim() : '');

/* Build stamps in the reader's own clock. The old line sliced the Z off an ISO string, which
   silently relabelled a UTC timestamp as local. */
export function localTime(v: unknown): string {
  if (!v) return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export const LEVELS: [number, string][] = [
  [8, 'Owner/Board'], [7, 'C-Suite'], [6, 'VP'], [5, 'Director/Head'], [4, 'Manager'],
  [3, 'Lead/Supervisor'], [2, 'Senior IC'], [1, 'IC'], [0, 'Entry/Support'],
  [-1, 'No title'], [-9, 'Unmapped'],
];
export const LVLNAME: Record<number, string> = Object.fromEntries(LEVELS);

/* GMR'S OWN VOCABULARY. The classifier's own set is decision maker / sponsor / influencer / user.
   GMR's is:
     AUTHORITY  can make the choice and sign off
     CHAMPION   no signing authority, but influence over the person who has it — typically a
                director
     INFLUENCER sent out to find the technology and do the due diligence, and selects the priority
                vendor. A level below a champion, and a word GMR does not otherwise use in-house,
                so inside Atlas it means what GMR means by it
     END USER   an operator, a supervisor, a superintendent or a plant manager
   Where the client already has a naming convention, even an informal one, the labelling matches it.
   A rep must never have to translate between two vocabularies.

   LABELS ONLY. The stored `icp_role` values, the classifier, the derived tag names and every API
   response are untouched. Renaming those would change the response contract and would mean the
   dataset no longer says what it said when it was audited. The display label changes; the data does
   not. The short form is also ambiguous on a control, where it abbreviates to DM. */
export const ROLE_SHORT: Record<string, string> = {
  decision_maker: 'AUTHORITY', sponsor: 'CHAMPION', influencer: 'INFLUENCER', user: 'END USER', not_relevant: '—',
};
/* The long form, for a control rather than a chip. */
export const ROLE_LABEL: Record<string, string> = {
  decision_maker: 'Authority', sponsor: 'Champion', influencer: 'Influencer', user: 'End user',
};
/* What each one MEANS, in the client's own terms. On the tile, on hover — a renamed control that does not say
   what it now means is ambiguous. */
export const ROLE_MEANS: Record<string, string> = {
  decision_maker: 'Can make the choice and sign off.',
  sponsor: 'No signing authority, but influence over the person who has it — typically a director.',
  influencer: 'Sent to find the tech and do the due diligence; selects the priority vendor.',
  user: 'Operator, supervisor, superintendent or plant manager — the people who run it.',
};
/* Four roles, four colours, and the SAME four everywhere in the app — the org chart's card stripe,
   the queue chip and the people list all read from here. A colour meaning a different thing on two
   screens is worse than no colour. */
export const ROLE_COLOR: Record<string, string> = {
  decision_maker: '#FFAA00', sponsor: '#FDB614', influencer: '#5B8DEF', user: '#3D4653',
};

/* `placed` counts buyers over the MASTER person table; every people list in this app holds
   reachable people only, so placed legitimately reads higher than the rows under it. The label
   discloses the lens — it does not change which column is shown. */
export const PLACED_NOTE = 'Counts the master list — every people list in this app shows reachable only, so this reads higher than the rows you can open.';

export const SQFT_NOTE = 'Sums the sq ft figures exactly as sent. A trusted figure exists on measured plants only — the rest are missing or failed the Sq ft trust check, so treat this total as unmeasured. See the Sq ft trust column on the Plants grid.';

/* The store holds E.164, and that is what a dial link and a paste both need — but +13135550142 is
   not a thing a human reads back off a screen. Grouped for the eye, never altered: the copy button
   and the tel: href both carry the stored value. */
export function phoneFmt(v: unknown): string {
  const s = String(v || '');
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(s);
  if (m) return `+1 ${m[1]} ${m[2]} ${m[3]}`;
  const i = /^\+(\d{1,3})(\d{6,})$/.exec(s);
  return i ? `+${i[1]} ${i[2].replace(/(\d{3})(?=\d)/g, '$1 ')}`.trim() : s;
}

/* ONE reader for "how far is this person from the plant". There were three of them in the old app
   and they did not agree. Two things are true about the number and both survive everywhere:
   · Zero is not a measurement. Both ends are TOWN CENTROIDS, so a person whose town matches the
     plant's town scores exactly 0 whether they are next door or across town. It reads "same town".
   · A distance built on a JOB record is soft — we had no location for the person and used the one
     on their job, which is usually the HQ or the posting office. It carries an asterisk. */
export function distText(mi: number | null | undefined, locationSource?: string | null):
{ text: string; soft: boolean; why: string } | null {
  if (mi == null) return null;
  const soft = locationSource === 'blitz_reverse_job';
  const why = (mi === 0
    ? 'Their town and the plant town are the same point in the gazetteer, so this is not a measured distance.'
    : 'Town centroid to town centroid.')
    + (soft ? ' We had no location for this person, so this uses the location on their JOB record, which is usually the company HQ or the posting office rather than where they sit. It is why the distance reads near zero.' : '');
  return { text: (mi === 0 ? 'same town' : N1(mi) + ' mi') + (soft ? '*' : ''), soft, why };
}

export const haversine = (a1: number, o1: number, a2: number, o2: number): number => {
  const R = 3958.8, dLat = ((a2 - a1) * Math.PI) / 180, dLon = ((o2 - o1) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos((a1 * Math.PI) / 180) * Math.cos((a2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};
